/**
 * WithdrawPage — 회원 탈퇴 (설정 → "탈퇴하기" 진입). Figma 2012:37780.
 *
 * 플로우:
 *   1. 탈퇴 사유 선택(단일) + 자유 입력 + 유의사항 확인 → "다음"
 *   2. "정말 탈퇴하시겠습니까?" destructive confirm (Figma 2023:13610)
 *      → "탈퇴하기" 클릭 시 탈퇴 API 호출
 *   3. 탈퇴 완료 오버레이(Figma 2023:13782) 3초 노출 후 auth clear + 홈 복귀
 *
 * "다음" 게이트: 사유 1개 선택 필수, "기타" 선택 시 자유 입력 필수,
 * "탈퇴 유의사항 확인 및 동의" 체크 필수 (Figma 2012:37806).
 *
 * "탈퇴 전 유의사항"은 어드민 약관 관리(TERMS_DIV=WITHDRAW_NOTICE)에서 관리 —
 * 미등록(404) 시 기본 하드코딩 문구(NOTICES)로 fallback. (GIFT_NOTICE 와 동일 패턴)
 *
 * 완료 화면을 3초 보여줘야 하므로 auth clear 는 API 직후가 아니라 오버레이 타이머
 * 종료 시점으로 미룬다. (clear 를 먼저 하면 RequireAuth 가 페이지를 즉시 언마운트해
 * 완료 화면과 타이머가 사라진다.)
 *
 * BE 는 `POST /members/me/withdraw` 로 구현돼 있다(MemberService.withdraw — soft delete +
 * refresh family 전량 폐기 + 기기 정리). 실패는 catch 에서 토스트 안내.
 * 정책: 탈퇴는 soft delete → adbe 스케줄러가 7일 경과분을 물리 파기(2026-09-14 결정).
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiPost } from '../../api/client';
import { fetchLatestTerms } from '../../api/terms';
import { currentLanguage } from '../../i18n';
import { sanitizeHtml } from '../../lib/sanitizeHtml';
import { useAuthStore } from '../../store/authStore';
import { clearOwnedLocalData } from '../../lib/localDataOwner';
import { useActivityNavigator } from '../../navigation/useActivityNavigator';
import { useToast } from '../../components/feedback/Toast';
import { Button } from "@astryxdesign/core/Button";
import { Text } from "@astryxdesign/core/Text";
import { CheckboxInput } from '@astryxdesign/core/CheckboxInput';
import { RadioList } from '@astryxdesign/core/RadioList';
import { RadioListItem } from '@astryxdesign/core/RadioList';
import ConfirmDialog from '../../components/feedback/ConfirmDialog';
import WithdrawDoneOverlay from './WithdrawDoneOverlay';
import styles from './WithdrawPage.module.css';

/**
 * code 는 BE(MemberService.WITHDRAW_REASON_CODES) 와 합의된 집합 — 문구만 i18n 이다.
 *
 * ⚠ 2026-09-07 — 라벨이 템플릿(픽쿠리) 것이었다: "원하는 상품이 없어요" · "금액이
 *   너무 비싸요". peeka 에는 파는 상품도 유료 결제도 없다. 상품 → 카드로 바꾸고
 *   `EXPENSIVE` 는 화면에서 뺐다(BE 는 여전히 그 코드를 받는다 — 응원 결제가
 *   열리면 그때 문구와 함께 되살린다).
 */
const REASON_CODES = ['LOW_USAGE', 'NO_PRODUCT', 'INCONVENIENT', 'ETC'] as const;
type ReasonCode = (typeof REASON_CODES)[number];
const OTHER_REASON_CODE: ReasonCode = 'ETC';
/** 자유 입력 최대 200자 (FN-082, 2026-08-28 확정 — 전에는 500) */
const REASON_DETAIL_MAX = 200;

/** 약관 관리(TERMS_DIV=WITHDRAW_NOTICE) 미등록 시 노출할 기본 유의사항. */
const WITHDRAW_NOTICE_DIVISION = 'WITHDRAW_NOTICE';

/**
 * ⚠ 2026-09-07 — 여기도 템플릿(픽쿠리) 문구였다: 보관함·선물함·배송·환불.
 *   peeka 에는 배송도 선물도 없다. 컬렉션·스캔 기록 기준으로 다시 썼다.
 */
const NOTICE_KEYS: readonly string[] = [
  'withdraw.notice.storage',
  'withdraw.notice.noLookup',
  'withdraw.notice.noRestore',
  'withdraw.notice.perProvider',
  'withdraw.notice.purge',
];

export default function WithdrawPage() {
  const [reasonCode, setReasonCode] = useState<ReasonCode | null>(null);
  const [reasonDetail, setReasonDetail] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  /**
   * 약관 관리(WITHDRAW_NOTICE)에서 관리하는 탈퇴 유의사항. 미등록이면 null → 기본 문구 fallback.
   *
   * 본문과 함께 **그 본문이 무슨 언어인지**(`languageCode`)도 들고 있는다. 요청 언어의
   * 번역본이 없으면 be 가 정본(한국어)을 내려보내는데, 그때 안내 없이 본문만 그리면
   * 영어·일본어 사용자에게는 이 화면만 번역이 빠진 것처럼 보인다(약관 뷰어는 이미
   * 같은 상황에서 `terms.originalNotice` 를 붙인다 — 2026-09-14).
   */
  const [notice, setNotice] = useState<{ html: string; languageCode: string } | null>(null);
  const [noticeAgreed, setNoticeAgreed] = useState(false);
  const clearAuth = useAuthStore((s) => s.clear);
  const nav = useActivityNavigator();
  const toast = useToast();
  const { t } = useTranslation(['app', 'common']);

  const language = currentLanguage();

  useEffect(() => {
    fetchLatestTerms(WITHDRAW_NOTICE_DIVISION, language)
      .then((res) =>
        setNotice(res?.bodyContent ? { html: res.bodyContent, languageCode: res.languageCode } : null),
      )
      .catch(() => setNotice(null));
  }, [language]);

  /**
   * 정본(한국어)이 내려온 경우에만 안내한다.
   *
   * 미등록(=null)일 때는 붙이지 않는다 — 그때 그리는 기본 문구(NOTICE_KEYS)는 i18n 이라
   * 이미 사용자 언어다. 약관 뷰어가 fallback 에도 안내를 붙이는 것과 다른 지점이다
   * (그쪽 정적 fallback 은 한국어 원문이다).
   */
  const showOriginalNotice = notice !== null && notice.languageCode !== language;

  const needsDetail = reasonCode === OTHER_REASON_CODE;
  const canProceed =
    reasonCode !== null && (!needsDetail || reasonDetail.trim().length > 0) && noticeAgreed;

  const handleConfirmWithdraw = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await apiPost('/members/me/withdraw', {
        reasonCode,
        reasonDetail: reasonDetail.trim() || undefined,
      });
      setConfirmOpen(false);
      setDone(true);
    } catch (e: unknown) {
      setConfirmOpen(false);
      setSubmitting(false);
      toast.error(e instanceof Error ? e.message : t('withdraw.failed'));
    }
  };

  // 완료 오버레이 타이머 종료 시점 — 이때 비로소 auth clear + 홈 복귀.
  const handleFinish = useCallback(() => {
    // 탈퇴 후 벤더 SDK 세션도 정리 — 재가입 시 이전 소셜 계정이 자동 재사용되지 않도록.
    // 네이티브 전용·best-effort(웹은 no-op). clear 전에 provider 를 읽는다.
    const provider = useAuthStore.getState().user?.provider ?? null;
    void import('../../native/socialAuth/signOut').then(({ signOutSocialVendors }) =>
      signOutSocialVendors(provider),
    );
    // 계정에 귀속된 로컬 기록(최근 스캔·검색어)도 함께 파기한다 — 서버만 지우면
    // 같은 기기의 다음 사용자에게 탈퇴한 사람의 기록이 남는다 (FN-082 · OI-48).
    // 게스트 스캔 카운트는 남긴다: "기기 기준 평생 5회"라 탈퇴로 초기화되면 안 된다.
    clearOwnedLocalData();
    clearAuth();
    nav.closeAllTo('/');
  }, [clearAuth, nav]);

  if (done) {
    return <WithdrawDoneOverlay onFinish={handleFinish} />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.scrollArea}>
        {/* 탈퇴 사유 */}
        <section className={styles.card}>
          <header className={styles.reasonHeader}>
            <h1 className={styles.reasonTitle}>{t('withdraw.reasonTitle')}</h1>
            <p className={styles.reasonSubtitle}>{t('withdraw.reasonSubtitle')}</p>
          </header>
          <div className={styles.reasonBody}>
            <RadioList
              label={t('withdraw.reasonLabel')}
              isLabelHidden
              value={reasonCode ?? ''}
              onChange={(v) => setReasonCode(v as ReasonCode)}
            >
              {REASON_CODES.map((code) => (
                <RadioListItem key={code} value={code} label={t(`withdraw.reason.${code}`)} />
              ))}
            </RadioList>
            <textarea
              className={styles.reasonInput}
              placeholder={t('withdraw.reasonPlaceholder')}
              value={reasonDetail}
              onChange={(e) => setReasonDetail(e.target.value)}
              rows={2}
              maxLength={REASON_DETAIL_MAX}
            />
            {/* n/200 카운터 (SC-034 · FN-082) — 컬렉션 이름·닉네임과 같은 문법.
                ⚠ 지역 CSS 를 새로 만들지 않는다 — Astryx Text 가 크기(supporting)·색
                  (secondary)·오른쪽 정렬(justify="end")을 다 갖고 있다. */}
            <Text type="supporting" display="block" justify="end">
              {t('withdraw.charCount', { len: reasonDetail.length, max: REASON_DETAIL_MAX })}
            </Text>
          </div>
        </section>

        {/* 탈퇴 전 유의사항 */}
        <section className={styles.card}>
          <header className={styles.noticeHeader}>
            <h2 className={styles.noticeTitle}>{t('withdraw.noticeTitle')}</h2>
          </header>
          {/* 번역본이 없어 한국어 정본이 내려온 경우의 안내. 지역 CSS 를 만들지 않는다 —
              위 글자수 카운터와 같이 Astryx Text(supporting·secondary)로 충분하다. */}
          {showOriginalNotice && (
            <Text type="supporting" color="secondary" display="block">
              {t('common:terms.originalNotice')}
            </Text>
          )}
          {notice ? (
            <div
              className={styles.noticeContent}
              // BE sanitize + FE DOMPurify 이중 방어 — 어드민 약관 본문 HTML.
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(notice.html) }}
            />
          ) : (
            <ul className={styles.noticeList}>
              {NOTICE_KEYS.map((key) => (
                <li key={key} className={styles.noticeItem}>
                  {t(key)}
                </li>
              ))}
            </ul>
          )}
          <div className={styles.agreeBox}>
            <CheckboxInput
              label={t('withdraw.agree')}
              value={noticeAgreed}
              onChange={(checked) => setNoticeAgreed(checked)}
            />
          </div>
        </section>
      </div>

      <div className={styles.cta}>
        <div className={styles.ctaButton}>
          <Button
            variant="secondary"
            size="lg"
            label={t('common:action.cancel')}
            clickAction={() => nav.close()}
            width="100%"
          />
        </div>
        <div className={styles.ctaButton}>
          <Button
            variant="primary"
            size="lg"
            label={t('common:action.next')}
            isDisabled={!canProceed}
            clickAction={() => setConfirmOpen(true)}
            width="100%"
          />
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        variant="destructive"
        title={t('withdraw.confirmTitle')}
        description={t('withdraw.confirmBody')}
        secondaryText={t('common:action.goBack')}
        primaryText={submitting ? t('withdraw.submitting') : t('withdraw.submit')}
        onClose={submitting ? undefined : () => setConfirmOpen(false)}
        onSecondaryPressed={submitting ? undefined : () => setConfirmOpen(false)}
        onPrimaryPressed={handleConfirmWithdraw}
      />
    </div>
  );
}
